import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { TwAccordion, TwAccordionDetails, TwAccordionSummary } from '../TwAccordion/TwAccordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { nanoid } from '@reduxjs/toolkit';
import { setOrderId, updateCommodity } from '../../redux/features/orders/ordersSlice';
import TwField, { TwInputTypes } from '../TwFields/TwFields';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import useNotifications from '../../providers/Notifications/Notifications.provider';

import useMediaQuery from '@mui/material/useMediaQuery';
import { SxProps, Theme } from '@mui/material/styles';
import './TwLoadCommodities.scss';

import { updateTripCommodity } from '../../redux/features/trip/tripSlice';
import { OrderCommodities } from '../../redux/api/orders/orders.model';
import { TripCommodities } from '../../redux/api/trips/trips.model';
import { EntityType } from '../../redux/Global.model';
import TwLoadTooltipInfo, { TwLoadTooltipInfoProps } from '../TwLoadTooltipInfo/TwLoadTooltipInfo';
import TwLoadTooltipContent from '../TwLoadTooltipInfo/TwLoadTooltipContent';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import TwAutocomplete, { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import { getPackagingUnitByLabel, packagingUnitOptions } from '../../utils/manifest/manifestUtils';
import TwTooltip from '../TwTooltip/TwTooltip';
import { TwMobileCommodityDetails } from '../TwMobileCommodityDetails/TwMobileCommodityDetails';

interface TwLoadCommoditiesProps {
  isImport: boolean;
  sxAccordion?: SxProps;
  title?: string;
  entity: EntityType;
  overlayView?: boolean;
}

const TwLoadCommodities: FC<TwLoadCommoditiesProps> = ({
  isImport,
  sxAccordion,
  title,
  entity
}): JSX.Element => {
  const { order_commodities } = useAppSelector((state) => state.orders);
  const { trip_commodities } = useAppSelector((state) => state.trip);
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const { enableBorderConnect } = useFeatureFlags();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const commoditiesToUse: TripCommodities[] =
    entity === EntityType.ORDER ? order_commodities : trip_commodities;

  const addNewCommodity = (): void => {
    if (commoditiesToUse.length === 20) {
      addNotification({
        message: 'You can only add up to 20 commodities',
        variant: 'error'
      });
    } else {
      const commoditiesCopy = [...commoditiesToUse];
      if (entity === EntityType.ORDER) {
        commoditiesCopy.push({
          ui_uid: nanoid(),
          description: '',
          quantity: null,
          weight: null,
          packaging_unit: null
        });
        dispatch(updateCommodity(commoditiesCopy));
      } else if (entity === EntityType.TRIP) {
        commoditiesCopy.push({
          ui_uid: nanoid(),
          description: '',
          customer: '',
          customer_number: '',
          order_id: '',
          order_readable_id: '',
          quantity: null,
          weight: null,
          packaging_unit: null
        });
        dispatch(updateTripCommodity(commoditiesCopy));
      }
    }
  };

  const updateCommodityData = (data: OrderCommodities | TripCommodities): void => {
    const commoditiesCopy = [...commoditiesToUse];
    const index = commoditiesCopy.findIndex((item) => item.ui_uid === data.ui_uid);
    commoditiesCopy[index] = data;

    entity === EntityType.ORDER
      ? dispatch(updateCommodity(commoditiesCopy))
      : dispatch(updateTripCommodity(commoditiesCopy));
  };

  const removeCommodity = (uid: string): void => {
    const commoditiesCopy = [...commoditiesToUse];
    const index = commoditiesCopy.findIndex((item) => item.ui_uid === uid);
    commoditiesCopy.splice(index, 1);
    entity === EntityType.ORDER
      ? dispatch(updateCommodity(commoditiesCopy))
      : dispatch(updateTripCommodity(commoditiesCopy));
  };

  useEffect(() => {
    if (commoditiesToUse.length === 0 && entity === EntityType.ORDER) {
      dispatch(
        updateCommodity([
          { ui_uid: nanoid(), description: '', quantity: null, weight: null, packaging_unit: null }
        ])
      );
    }

    if (commoditiesToUse.length === 0 && entity === EntityType.TRIP) {
      dispatch(
        updateTripCommodity([
          {
            ui_uid: nanoid(),
            description: '',
            customer: '',
            customer_number: '',
            order_id: '',
            order_readable_id: '',
            order_commodity_id: '',
            quantity: null,
            weight: null,
            packaging_unit: null
          }
        ])
      );
    }
  }, [commoditiesToUse]);

  const totalWeight = useMemo(() => {
    return commoditiesToUse.reduce((total: number, item: any) => {
      return total + (item.weight ? Number(item.weight) : 0);
    }, 0);
  }, [commoditiesToUse]);

  const totalQty = useMemo(() => {
    return commoditiesToUse.reduce((total, item) => {
      return total + (item.quantity ? Number(item.quantity) : 0);
    }, 0);
  }, [commoditiesToUse]);

  const CommodityFooter = (): JSX.Element => {
    return (
      <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
        <Grid
          item
          xs={
            matchesMobileOrTablet
              ? 4
              : entity === EntityType.TRIP
              ? 3
              : enableBorderConnect
              ? 5.2
              : 6.2
          }
        >
          <TwButton
            startIcon={<AddIcon />}
            label="Add item"
            type={TwButtonType.text}
            action={addNewCommodity}
          ></TwButton>
        </Grid>
        <Grid
          item
          xs={
            matchesMobileOrTablet
              ? 0.8
              : entity === EntityType.TRIP
              ? 6.2
              : enableBorderConnect
              ? 4
              : 0
          }
        ></Grid>
        <Grid
          item
          xs={
            matchesMobileOrTablet ? 3.2 : entity === EntityType.TRIP || enableBorderConnect ? 1 : 2
          }
          sx={matchesMobileOrTablet ? { fontWeight: 'bold' } : {}}
        >
          <span>{!totalQty ? '' : totalQty}</span>
        </Grid>

        <Grid
          item
          xs={
            matchesMobileOrTablet
              ? 2.9
              : entity === EntityType.TRIP || enableBorderConnect
              ? 1.5
              : 3
          }
          sx={matchesMobileOrTablet ? { fontWeight: 'bold' } : {}}
          className="load-commodity__container-total"
        >
          <span>{!totalWeight ? '' : totalWeight}</span>
          <span className="load-commodity__total">{matchesMobileOrTablet ? 'lbs' : 'TOTAL'}</span>
        </Grid>
      </Grid>
    );
  };

  return (
    <TwAccordion defaultExpanded={true} sx={{ backgroundColor: '#F3F4F6', ...sxAccordion }}>
      <TwAccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => undefined} />}>
        <Typography
          sx={{
            fontSize: '12px',
            weight: '500',
            color: '#6B7280',
            marginLeft: '10px'
          }}
        >
          {title ? title : 'Order information'}
        </Typography>
      </TwAccordionSummary>
      <TwAccordionDetails>
        {!matchesMobileOrTablet && (
          <Grid
            container
            className={`load-commodity__header${
              isImport || matchesMobileOrTablet ? '-mobile' : ''
            }`}
          >
            <Grid item xs={entity === EntityType.TRIP ? 3 : enableBorderConnect ? 5 : 6}>
              <span>DESCRIPTION </span>
            </Grid>
            {entity === EntityType.TRIP && (
              <>
                <Grid item xs={enableBorderConnect ? 2 : 3}>
                  <span>CUSTOMER</span>
                </Grid>
                <Grid item xs={enableBorderConnect ? 1 : 2}>
                  <span>CUST #</span>
                </Grid>
                <Grid item xs={1}>
                  <span>ORDER #</span>
                </Grid>
              </>
            )}
            {enableBorderConnect && (
              <Grid item xs={entity === EntityType.TRIP ? 2 : 4}>
                <span>UNIT</span>
              </Grid>
            )}
            <Grid item xs={entity === EntityType.TRIP || enableBorderConnect ? 1 : 2}>
              <span>QTY</span>
            </Grid>
            <Grid item xs={entity === EntityType.TRIP || enableBorderConnect ? 2 : 4}>
              <span>WEIGHT</span>
            </Grid>
          </Grid>
        )}

        {matchesMobileOrTablet ? (
          <>
            <TwMobileCommodityDetails
              entity={entity}
              commodities={commoditiesToUse}
              onDataUpdate={updateCommodityData}
              onRemove={removeCommodity}
            />
            <CommodityFooter />
          </>
        ) : (
          <Box className="load-commodity__details-container">
            {commoditiesToUse.map((item: OrderCommodities | TripCommodities, index: number) => {
              return (
                <LoadCommodityRow
                  entity={entity}
                  key={item.ui_uid}
                  commodity={item}
                  isFirstRow={index === 0}
                  showRemoveButton={commoditiesToUse.length > 1}
                  onDataUpdate={updateCommodityData}
                  onRemove={removeCommodity}
                  isImport={isImport}
                />
              );
            })}
            <CommodityFooter />
          </Box>
        )}
      </TwAccordionDetails>
    </TwAccordion>
  );
};
export default TwLoadCommodities;

interface CommodityRowProps {
  commodity: TripCommodities;
  key: string;
  showRemoveButton: boolean;
  isFirstRow: boolean;
  isImport: boolean;
  entity: EntityType;
  onDataUpdate: (data: OrderCommodities | TripCommodities) => void;
  onRemove: (id: string) => void;
}

const LoadCommodityRow: FC<CommodityRowProps> = ({
  commodity,
  showRemoveButton,
  isFirstRow,
  isImport,
  entity,
  onDataUpdate,
  onRemove
}): JSX.Element => {
  const [description, setDescription] = useState<string | null>(commodity.description);
  const [packagingUnit, setPackagingUnit] = useState<TwAutocompleteOption | null>(
    getPackagingUnitByLabel(commodity.packaging_unit)
  );
  const [quantity, setQuantity] = useState<number | null>(commodity.quantity);
  const [weight, setWeight] = useState<number | null>(commodity.weight);
  const { enableBorderConnect } = useFeatureFlags();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const truncateLength = matchesMobileOrTablet ? 6 : 12;

  return (
    <Grid container className="load-commodity__row">
      <Grid
        item
        xs={entity === EntityType.TRIP ? 3 : enableBorderConnect ? 5 : 6}
        alignContent={'center'}
      >
        {commodity.order_id ? (
          <span>{commodity.description || '-'}</span>
        ) : (
          <TwField
            value={description || ''}
            name="commodity-description"
            onInputChange={(e): void => setDescription(e.target.value)}
            onInputBlur={(e): void => {
              onDataUpdate({ ...commodity, description: description });
            }}
          />
        )}
      </Grid>
      {entity === EntityType.TRIP && (
        <>
          <Grid item xs={enableBorderConnect ? 2 : 3} alignContent={'center'}>
            <span>{commodity.customer || '-'}</span>
          </Grid>
          <Grid item xs={enableBorderConnect ? 1 : 2} alignContent={'center'}>
            <span title={commodity.customer_number || '-'}>
              {commodity.customer_number
                ? commodity.customer_number.length > truncateLength
                  ? `${commodity.customer_number.slice(0, truncateLength)}...`
                  : commodity.customer_number
                : '-'}
            </span>
          </Grid>
          <Grid item xs={1} alignContent={'center'}>
            <span>{commodity.order_readable_id || '-'}</span>
          </Grid>
        </>
      )}
      {enableBorderConnect && (
        <Grid item xs={entity === EntityType.TRIP ? 2 : 4}>
          <TwAutocomplete
            id="commodity-packaging-unit"
            name="commodity-packaging-unit"
            optionList={packagingUnitOptions}
            value={packagingUnit}
            onChange={(_, newValue): void => {
              setPackagingUnit(newValue);
              onDataUpdate({ ...commodity, packaging_unit: newValue?.label ?? null });
            }}
          />
        </Grid>
      )}
      <Grid item xs={entity === EntityType.TRIP || enableBorderConnect ? 1 : 2}>
        <TwField
          inputType={TwInputTypes.number}
          value={quantity || ''}
          name="commodity-quantity"
          onInputChange={(e): void => setQuantity(parseInt(e.target.value))}
          onInputBlur={(e): void => {
            onDataUpdate({ ...commodity, quantity: quantity });
          }}
        />
      </Grid>
      <Grid item xs={entity === EntityType.TRIP ? 2 : enableBorderConnect ? 2 : 4}>
        <TwField
          inputType={TwInputTypes.number}
          value={weight || ''}
          name="commodity-weight"
          onInputChange={(e): void => setWeight(parseInt(e.target.value))}
          endAdorment="lbs"
          className={`load-commodity__weight${isImport || matchesMobileOrTablet ? '-mobile' : ''}`}
          onInputBlur={(e): void => {
            onDataUpdate({ ...commodity, weight: weight });
          }}
        />
      </Grid>

      {showRemoveButton && !isFirstRow && (
        <IconButton
          disableRipple
          aria-label="remove-commodity"
          className={`load-commodity__remove${isImport || matchesMobileOrTablet ? '-mobile' : ''}`}
          onClick={(): void => onRemove(commodity.ui_uid)}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Grid>
  );
};

export const TwLoadCommodityReadOnly: FC<{
  commodities: TripCommodities[];
  ordersTooltipDataArray?: TwLoadTooltipInfoProps[];
  entity: EntityType;
  showTitle?: boolean;
  overlayView?: boolean;
  disableActions?: boolean;
  changeTab?: (event: React.SyntheticEvent, newValue: string, isClickFromTab: boolean) => void;
}> = ({
  commodities,
  ordersTooltipDataArray,
  showTitle = true,
  entity,
  overlayView,
  disableActions = false,
  changeTab
}) => {
  const dispatch = useAppDispatch();
  const { enableBorderConnect } = useFeatureFlags();

  const isOrderOverlayOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);
  const isTripOverlayOpen = useAppSelector((state) => state.navigation.isTripDetailOpen);

  const totalWeight = commodities.reduce((total, item) => {
    return total + (item.weight ? Number(item.weight) : 0);
  }, 0);

  const totalQty = commodities.reduce((total, item) => {
    return total + (item.quantity ? Number(item.quantity) : 0);
  }, 0);

  const isTripOverlay = overlayView && entity === EntityType.TRIP;

  const handleOrderClick = (e: SyntheticEvent, orderId: string): void => {
    if (isOrderOverlayOpen) {
      dispatch(setOrderId(orderId));
      changeTab && changeTab(e, '0', true);
      return;
    }

    if (isTripOverlayOpen) {
      changeTab && changeTab(e, orderId, false);
      return;
    }
  };

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const truncateLength = matchesMobileOrTablet ? 6 : 12;

  const isTripCommodity = (item: OrderCommodities | TripCommodities): item is TripCommodities => {
    return 'customer' in item;
  };

  return (
    <>
      {showTitle && <Box className="load-detail__data-container__data--title">Commodities</Box>}
      <Box sx={{ marginTop: '20px' }}>
        <Grid container className="load-commodity__read-header">
          <Grid item xs={isTripOverlay ? 3 : enableBorderConnect ? 5 : 7}>
            <span>COMMODITY</span>
          </Grid>
          {enableBorderConnect && (
            <Grid item xs={4}>
              <span>UNIT</span>
            </Grid>
          )}
          <Grid item xs={isTripOverlay || enableBorderConnect ? 1 : 2}>
            <span>QTY</span>
          </Grid>
          <Grid item xs={isTripOverlay || enableBorderConnect ? 2 : 3}>
            <span>WEIGHT</span>
          </Grid>
          {isTripOverlay && (
            <Grid item xs={2}>
              <span>CUSTOMER</span>
            </Grid>
          )}
          {isTripOverlay && (
            <Grid item xs={2}>
              <span>CUST. #</span>
            </Grid>
          )}
          {isTripOverlay && (
            <Grid item xs={1}>
              <span>ORDER #</span>
            </Grid>
          )}
        </Grid>

        {commodities.map((item: OrderCommodities | TripCommodities) => {
          const tripItem = isTripCommodity(item) ? item : null;
          return (
            <Grid container className="load-commodity__read-row" key={item.ui_uid}>
              <Grid item xs={isTripOverlay ? 3 : enableBorderConnect ? 5 : 7}>
                {item.description || '-'}
              </Grid>
              {enableBorderConnect && (
                <Grid item xs={4}>
                  {item.packaging_unit ?? '-'}
                </Grid>
              )}
              <Grid item xs={isTripOverlay || enableBorderConnect ? 1 : 2}>
                {item.quantity === 0 || !item.quantity ? '-' : item.quantity}
              </Grid>
              <Grid item xs={isTripOverlay || enableBorderConnect ? 2 : 3}>
                {item.weight === 0 || !item.weight ? '-' : `${item.weight} lb`}
              </Grid>
              {isTripOverlay && (
                <>
                  <Grid item xs={matchesMobileOrTablet ? 1.5 : 2}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                    >
                      {tripItem?.customer || '-'}
                    </Box>
                  </Grid>
                  <Grid item xs={matchesMobileOrTablet ? 1.5 : 2}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                    >
                      {tripItem?.customer_number?.length &&
                      tripItem?.customer_number?.length > truncateLength ? (
                        <TwTooltip title={`${tripItem?.customer_number}`}>
                          <Typography
                            component={'span'}
                            sx={{
                              fontSize: '14px'
                            }}
                          >
                            {`${tripItem?.customer_number?.slice(0, truncateLength) ?? ''}...`}
                          </Typography>
                        </TwTooltip>
                      ) : (
                        tripItem?.customer_number ?? '-'
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={matchesMobileOrTablet ? 1.2 : 1}>
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                    >
                      {disableActions ? (
                        <Typography sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}>
                          {tripItem?.order_readable_id || '-'}
                        </Typography>
                      ) : (
                        <TwLoadTooltipInfo
                          tooltipContent={
                            <TwLoadTooltipContent
                              data={ordersTooltipDataArray?.find(
                                (d) => d.loadId === tripItem?.order_id
                              )}
                            />
                          }
                        >
                          <Typography
                            sx={{ color: '#3A4DE9', fontSize: '14px', cursor: 'pointer' }}
                            onClick={(e): void => {
                              handleOrderClick(e, tripItem?.order_id || '');
                            }}
                          >
                            {tripItem?.order_readable_id || '-'}
                          </Typography>
                        </TwLoadTooltipInfo>
                      )}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          );
        })}

        <Grid container className="load-commodity__read-total">
          <Grid item xs={isTripOverlay ? 3 : enableBorderConnect ? 4.9 : 7}>
            <span>TOTAL</span>
          </Grid>
          {enableBorderConnect && <Grid item xs={4}></Grid>}
          <Grid item xs={isTripOverlay || enableBorderConnect ? 1 : 2}>
            {totalQty}
          </Grid>
          <Grid item xs={isTripOverlay || enableBorderConnect ? 1.5 : 3}>
            {totalWeight} lb
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
